import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabaseClient';

const C = { turquesa: '#5dc1b9', mango: '#FDBE02', coral: '#eb636b' };
const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";

// Extract YouTube video ID from various URL formats
function extractYouTubeId(url) {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  // Maybe it's just the ID itself
  if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) return url.trim();
  return null;
}

export default function AdminRadarVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [urls, setUrls] = useState({});

  useEffect(() => {
    loadVideos();
  }, []);

  const loadVideos = async () => {
    const { data, error } = await supabase
      .from('radar_videos')
      .select('*')
      .order('sort_order');
    if (error) {
      console.error('[AdminRadarVideos] Load error:', error.message);
    } else {
      setVideos(data || []);
      const urlMap = {};
      (data || []).forEach(v => { urlMap[v.id] = v.youtube_url || ''; });
      setUrls(urlMap);
    }
    setLoading(false);
  };

  const saveVideo = async (video) => {
    const url = urls[video.id] || '';
    const youtubeId = extractYouTubeId(url);

    setSaving(prev => ({ ...prev, [video.id]: true }));
    const { error } = await supabase
      .from('radar_videos')
      .update({
        youtube_url: url || null,
        youtube_id: youtubeId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', video.id);

    if (error) {
      console.error('[AdminRadarVideos] Save error:', error.message);
      alert('Error al guardar: ' + error.message);
    } else {
      await loadVideos();
    }
    setSaving(prev => ({ ...prev, [video.id]: false }));
  };

  if (loading) {
    return <p style={{ color: 'rgba(255,255,255,0.5)', fontFamily: font }}>Cargando videos...</p>;
  }

  return (
    <div>
      <h2 style={{ fontFamily: serif, fontSize: 24, fontWeight: 400, margin: '0 0 8px', color: '#fff' }}>
        Videos Tu Radar
      </h2>
      <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: '0 0 24px', fontFamily: font }}>
        Pega la URL de YouTube para cada video. El ID se extrae automáticamente.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {videos.map(video => {
          const currentUrl = urls[video.id] || '';
          const extractedId = extractYouTubeId(currentUrl);
          const isSaving = saving[video.id];

          return (
            <div key={video.id} style={{
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16,
              padding: '20px',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 50,
                  background: 'rgba(253,190,2,0.12)', color: C.mango, fontFamily: font,
                }}>{video.slot.toUpperCase()}</span>
                <p style={{ fontSize: 15, fontWeight: 500, margin: 0, fontFamily: font, color: '#fff' }}>{video.title}</p>
                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginLeft: 'auto' }}>
                  Día {video.unlock_day} · {video.duration}
                </span>
              </div>

              {/* URL input */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input
                  value={currentUrl}
                  onChange={e => setUrls(prev => ({ ...prev, [video.id]: e.target.value }))}
                  placeholder="https://youtube.com/watch?v=... o ID del video"
                  style={{
                    flex: 1, padding: '10px 14px', fontSize: 13,
                    background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.1)',
                    borderRadius: 10, color: '#fff', outline: 'none', fontFamily: font,
                  }}
                />
                <button
                  onClick={() => saveVideo(video)}
                  disabled={isSaving}
                  style={{
                    padding: '10px 18px', fontSize: 13, fontWeight: 500,
                    background: C.turquesa, color: '#fff', border: 'none', borderRadius: 10,
                    cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: font,
                    opacity: isSaving ? 0.6 : 1,
                  }}
                >
                  {isSaving ? '...' : 'Guardar'}
                </button>
              </div>

              {/* Extracted ID + status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: extractedId ? 12 : 0 }}>
                {currentUrl && (
                  <span style={{
                    fontSize: 11, fontFamily: font,
                    color: extractedId ? C.turquesa : C.coral,
                  }}>
                    {extractedId ? `✓ ID: ${extractedId}` : '✗ URL no reconocida'}
                  </span>
                )}
                {video.youtube_id && !currentUrl && (
                  <span style={{ fontSize: 11, color: C.turquesa, fontFamily: font }}>
                    ✓ Configurado: {video.youtube_id}
                  </span>
                )}
              </div>

              {/* Preview embed */}
              {extractedId && (
                <div style={{
                  position: 'relative', paddingBottom: '56.25%', height: 0,
                  borderRadius: 12, overflow: 'hidden', background: '#000',
                }}>
                  <iframe
                    src={`https://www.youtube.com/embed/${extractedId}?rel=0&modestbranding=1`}
                    title={`Preview: ${video.title}`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
